import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { supabase } from '../services/supabase/client'
import { Platform } from 'react-native'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { OnboardingState } from '../types'
import { v4 as uuidv4 } from 'uuid'

const initialState = {
  // State
  currentStep: 0,
  isComplete: false,
  hasCheckedProfile: false,

  // User Info
  fullName: '',
  displayName: '',
  photoURL: null,

  // Preferences
  language: 'en',
  currency: 'USD',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '12',
  numberFormat: 'thousand_dot'
}

// Create a storage object that works for both web and mobile
const storage = {
  getItem: async (name: string) => {
    try {
      if (Platform.OS === 'web') {
        const value = localStorage.getItem(name)
        return value ? Promise.resolve(value) : Promise.resolve(null)
      } else {
        const value = await AsyncStorage.getItem(name)
        return value
      }
    } catch (err) {
      console.error('Error getting item from storage:', err)
      return null
    }
  },
  setItem: async (name: string, value: string) => {
    try {
      if (Platform.OS === 'web') {
        localStorage.setItem(name, value)
        return Promise.resolve()
      } else {
        await AsyncStorage.setItem(name, value)
      }
    } catch (err) {
      console.error('Error setting item in storage:', err)
    }
  },
  removeItem: async (name: string) => {
    try {
      if (Platform.OS === 'web') {
        localStorage.removeItem(name)
        return Promise.resolve()
      } else {
        await AsyncStorage.removeItem(name)
      }
    } catch (err) {
      console.error('Error removing item from storage:', err)
    }
  }
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCurrentStep: (step) => set({ currentStep: step }),
      setComplete: (value) => set({ isComplete: value }),
      updateUserInfo: async (info, userId) => {
        try {
          // First, get the current state
          const currentState = get()
          
          // Merge the new info with current state
          const updatedState = {
            fullName: info.fullName || currentState.fullName,
            displayName: info.displayName || currentState.displayName,
            photoURL: info.photoURL || currentState.photoURL,
            language: info.language || currentState.language,
            currency: info.currency || currentState.currency,
            dateFormat: info.dateFormat || currentState.dateFormat,
            timeFormat: info.timeFormat || currentState.timeFormat,
            numberFormat: info.numberFormat || currentState.numberFormat,
          }
          
          // Prepare profile data - only include fields that exist in the profiles table
          const profileData = {
            full_name: updatedState.fullName,
            display_name: updatedState.displayName,
            photo_url: updatedState.photoURL,
            updated_at: new Date().toISOString()
          }

          // Check if profile exists
          const { data: existingProfile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userId)
            .single();

          // Save to profile settings
          if (existingProfile) {
            const { error } = await supabase
              .from('profiles')
              .update(profileData)
              .eq('id', userId);
              
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('profiles')
              .insert({
                id: userId,
                ...profileData,
                created_at: new Date().toISOString()
              });
              
            if (error) throw error;
          }

          // Check if user preferences exist
          const { data: existingPreferences } = await supabase
            .from('user_preferences')
            .select('id')
            .eq('user_id', userId)
            .single();

          // Update user preferences in user_preferences table
          const { error: settingsError } = await supabase
            .from('user_preferences')
            .upsert({
              id: existingPreferences?.id || uuidv4(), // Use existing ID or generate new one
              user_id: userId,
              language: updatedState.language,
              currency: updatedState.currency,
              theme: 'system',
              date_format: updatedState.dateFormat,
              time_format: updatedState.timeFormat,
              number_format: updatedState.numberFormat,
              updated_at: new Date().toISOString()
            });
            
          if (settingsError) throw settingsError;

          // After successful Supabase update, update local state
          set((state) => ({
            ...state,
            ...updatedState,
            hasCheckedProfile: true
          }))

          // Save language preference
          if (Platform.OS === 'web') {
            localStorage.setItem('i18nextLng', updatedState.language)
          } else {
            await AsyncStorage.setItem('i18nextLng', updatedState.language)
          }

          return true
        } catch (error) {
          console.error('Error updating user info:', error)
          return false
        }
      },
      checkUserProfile: async (userId) => {
        if (!userId) return false
        
        // If we've already checked the profile and it's complete, don't check again
        const state = get()
        if (state.hasCheckedProfile && state.isComplete) {
          return true
        }
        
        try {
          // Check profile in profiles table
          const { data: profileData, error } = await supabase
            .from('profiles')
            .select('*, onboarding_completed')
            .eq('id', userId)
            .single();
          
          if (error || !profileData || !profileData.full_name) {
            set({ ...initialState, hasCheckedProfile: true })
            return false
          }

          // Get user settings from user_preferences table
          const { data: userPreferences, error: preferencesError } = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', userId)
            .single();
            
          // Default settings if not found
          const preferences = preferencesError ? {
            language: 'en',
            currency: 'USD',
            date_format: 'MM/dd/yyyy',
            time_format: '12',
            number_format: 'thousand_dot'
          } : userPreferences;
          
          // Check if onboarding is completed
          const isOnboardingCompleted = profileData.onboarding_completed === true;
          
          // If user has a profile, update local state
          set({
            isComplete: isOnboardingCompleted, // Use the onboarding_completed flag from the database
            hasCheckedProfile: true,
            fullName: profileData.full_name,
            displayName: profileData.display_name || '',
            photoURL: profileData.photo_url || null,
            language: preferences.language || 'en',
            currency: preferences.currency || 'USD',
            dateFormat: preferences.date_format || 'MM/dd/yyyy',
            timeFormat: preferences.time_format || '12',
            numberFormat: preferences.number_format || 'thousand_dot'
          })
          
          return isOnboardingCompleted // Return true only if onboarding is completed
        } catch (error) {
          console.error('Error checking user profile:', error)
          set({ ...initialState, hasCheckedProfile: true })
          return false
        }
      },
      reset: () => {
        set(initialState)
      },
      cleanup: () => {
        storage.removeItem('flows-onboarding')
        set(initialState)
      }
    }),
    {
      name: 'flows-onboarding',
      storage: createJSONStorage(() => storage),
      partialize: (state) => ({
        isComplete: state.isComplete,
        currentStep: state.currentStep,
        fullName: state.fullName,
        displayName: state.displayName,
        photoURL: state.photoURL,
        language: state.language,
        currency: state.currency,
        dateFormat: state.dateFormat,
        timeFormat: state.timeFormat,
        hasCheckedProfile: state.hasCheckedProfile
      }),
      version: 1
    }
  )
) 